
import mondaySdk from 'monday-sdk-js';
import { API, axios_v2, getSessionToken } from '../utils/http';
import { hydrateTextVal } from '../utils/mondayDataParser';
const monday = mondaySdk();

export const mondaySetItem = async (key: string, value: any) => {
  return new Promise((resolve, reject) => {
    monday.storage.instance
      .setItem(key, value)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

export const mondayGetItem = async (
  key: string,
): Promise<{ data: { value: string } }> => {
  return new Promise((resolve, reject) => {
    monday.storage.instance
      .getItem(key)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

export const getFileDetails = async ({
  boardId,
  itemId,
  columnId,
}: {
  boardId: number;
  itemId: number;
  columnId: number;
}) => {
  const response = await API(
    `query getFileDetails($boardId: [ID!], $itemId: [ID!], $columnId: [String!]) {
      boards(ids: $boardId){
        items_page(limit: 500, query_params: {ids: $itemId}) {
          items {
            column_values (ids: $columnId){
              id
              text
              value
              type
              ... on EmailValue {
                  email
                  updated_at
              }
              ... on MirrorValue {
                  display_value
                  id
              }
              ... on FormulaValue {
                  id
                  value
              }
              ... on BoardRelationValue{
                  display_value
                  id
                  value
              }
            }
          }
        }
      }
    }`,
    {
      boardId: [Number(boardId)],
      itemId: [Number(itemId)],
      columnId: [columnId],
    },
  );

  const modifiedResponse = {
    ...response,
    data: {
      ...response?.data,
      boards: response?.data.boards.map((board: any) => ({
        ...board,
        items_page: {
          ...board.items_page,
          items: board.items_page.items.map((item: any) => ({
            ...item,
            column_values: hydrateTextVal(item.column_values),
          })),
        },
      })),
    },
  };

  return modifiedResponse;
};

export const getBoardPermission = async (
  boardId: number,
  userId: string,
): Promise<boolean> => {
  const query = `
      query getBoardPermission($boardId: [ID!]) {
        boards(ids: $boardId) {
          name
          id
          owners {
            id
          }
        }
      }
    `;
  const response = await API(query, {
    boardId: Number(boardId),
  });
  const owners = response?.data?.boards[0]?.owners?.map((x: any) => x.id) || [];
  return !!owners.includes(userId);
};

export const updateMondayInputColumn = async ({
  boardId,
  itemId,
  columnId,
  value,
}: {
  boardId: number;
  itemId: number;
  columnId?: string;
  value: string;
}) => {
  if (!columnId) return;
  return await API(
    `mutation UPDATE_COLUMN($boardId: ID!, $itemId: ID, $columnId: String!, $value: String!) {
      change_simple_column_value(
        board_id: $boardId
        item_id: $itemId
        column_id: $columnId
        value: $value
      ) {
        id
      }
    }`,
    {
      boardId: Number(boardId),
      itemId: itemId,
      columnId: columnId,
      value: value,
    },
  );
};

export const updateMondayColumnValue = async ({
  boardId,
  itemId,
  columnId,
  value,
}: {
  boardId: number;
  itemId: number;
  columnId: string;
  value: string;
}) => {
  return await API(
    `mutation CHANGE_COLUMN_VALUE($boardId: ID!, $itemId: ID, $columnId: String!, $value: JSON!) {
      change_column_value(board_id: $boardId, item_id: $itemId, column_id: $columnId, value: $value) {
        id
      }
    }`,
    {
      boardId: Number(boardId),
      itemId,
      columnId,
      value,
    },
  );
};

export const updateMondayFileColumn = async ({
  itemId,
  columnId,
  file,
}: {
  itemId: number;
  columnId: string;
  file: File;
}) => {
  try {
    return await API(
      `mutation UPDATE_FILE_COLUMN($itemId: ID!, $columnId: String!, $file: File!) {
      add_file_to_column(item_id: $itemId, column_id: $columnId, file: $file) {
        id
      }
    }`,
      {
        itemId: itemId,
        columnId: columnId,
        file: file,
      },
    );
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getFormulaValues = async ({
  envelopeId,
  itemId,
  fileId,
}: {
  envelopeId: string;
  itemId: number;
  fileId: string;
}) => {
  const token = await getSessionToken();
  const res = await axios_v2.get(
    `/file/calculate-formula/${envelopeId}/${itemId}/${fileId}`,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        sessionToken: token?.data,
      },
    },
  );
  return res?.data;
};

export const deleteMondayFileColumn = async ({
  boardId,
  itemId,
  columnId,
}: {
  boardId: number;
  itemId: number;
  columnId: string;
}) => {
  try {
    return await API(
      `mutation CHANGE_COLUMN_VALUE($boardId: ID!, $itemId: ID, $columnId: String!, $value: JSON!){
        change_column_value(board_id: $boardId, item_id: $itemId, value: $value, column_id: $columnId){
          id
        }
      }`,
      {
        boardId: boardId,
        itemId: itemId,
        columnId: columnId,
        value: '{"clear_all": true}',
      },
    );
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getSubscriptionStatus = async () => {
  return await API(`
    query GET_SUBSCRIPTION_DETAILS {
      app_subscription {
        billing_period
        days_left
        is_trial
        plan_id
        renewal_date
        pricing_version
      }
    }
    `);
};

export const getItemName = async (itemId: number) => {
  return await API(
    `
      query GET_ITEM($ids:[ID!]){
        items(ids:$ids){
          id
          name
        }
      }
      `,
    {
      ids: [itemId],
    },
  );
};

export const getUserNames = async (
  userIds: number[],
): Promise<Array<{ id: string; name: string; email: string }>> => {
  if (!userIds || userIds.length === 0) return [];
  const response = await API(
    `
      query getUserNames($ids: [ID!]){
        users(ids: $ids){
          id
          name
          email
        }
      }
      `,
    {
      ids: userIds.map((id) => String(id)),
    },
  );
  return response?.data?.users || [];
};
